import ModalController from '@/public/components/Modal/controller'
import { Controller } from '@hotwired/stimulus'
import { DataResult } from './types'

const DEFAULT_ERROR_MESSAGE = 'Произошла ошибка при попытке получить данные, попробуйте перезагрузить страницу'

export default class extends Controller {
    static targets = [
        'slider',
        'sliderPagination',
    ]

    declare readonly sliderTarget: HTMLElement
    declare readonly hasSliderTarget: boolean
    declare readonly hasSliderPaginationTarget: boolean
    declare readonly sliderPaginationTarget: HTMLElement

    private swiper: any | null = null

    connect(): void {
        document.addEventListener('lot-view-modal:open', this.handleOpenEvent as EventListener)
    }

    disconnect(): void {
        document.removeEventListener('lot-view-modal:open', this.handleOpenEvent as EventListener)
    }

    handleOpenEvent = (event: Event) => {
        const auctionId = (event as CustomEvent).detail?.auctionId

        if (!auctionId) {
            return
        }

        this.open(parseInt(auctionId))
    }

    async open(auctionId: number) {
        const modalController = this.resolveModalController()

        if (!modalController || !auctionId) {
            return
        }

        modalController.setContent('<div class="lot-view__loader"><span class="icon loading"></span></div>')
        modalController.open()

        const { html, error } = await this.loadAuctionData(auctionId)

        if (error || !html) {
            window.dispatchEvent(new CustomEvent('alert:show', { detail: { message: error || DEFAULT_ERROR_MESSAGE, type: 'error', timeout: 5000 }}))
            return modalController.hide()
        }

        modalController.setContent(html)
    }

    private resolveModalController(): ModalController | null {
        // @ts-ignore
        return this.application.getControllerForElementAndIdentifier(this.element, 'modal') as ModalController | null
    }

    private async loadAuctionData(auctionId: number): Promise<DataResult> {
        try {
            const response = await fetch(`/lots/auction-view/${auctionId}`, {
                headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
                method: 'GET'
            })

            const { error, html } = (await response.json()) as DataResult

            if (error || !html) {
                return { error: error || DEFAULT_ERROR_MESSAGE }
            }

            return { html }
        } catch {
            return { error: DEFAULT_ERROR_MESSAGE }
        }
    }

    public async sliderTargetConnected(element: HTMLElement): Promise<void> {
        const [{ default: Swiper }, { Pagination }] = await Promise.all([
            import('swiper'),
            import('swiper/modules')
        ])

        await import('swiper/swiper-bundle.css')

        this.swiper = new Swiper(element, {
            modules: [Pagination],
            spaceBetween: 0,
            grabCursor: true,
            slidesPerView: 1,
            loop: element.querySelectorAll('.swiper-slide').length > 1,
            autoHeight: true,
            pagination: this.hasSliderPaginationTarget ? { el: this.sliderPaginationTarget, clickable: true } : false
        })
    }

    public sliderTargetDisconnected(element: HTMLElement): void {
        this.swiper?.destroy()
    }
}
